<template>
  <div :class="{
    'calendar-content': true,
    'has-visible-notes': notesVisible,
    'flight-details-is-open': !!selectedFlight
  }">
    <div
      v-if="warningsVisible"
      :class="{ 'calendar-content-warnings': true, 'is-loading': !warningsLoaded }"
      :data-is-odd="warningsAmountIsOdd">
      <template v-for="level of $options.warningLevels">
        <template v-if="warnings[level]">
          <div
            v-for="(warningsGroup, index) of warnings[level]"
            :key="'calendar-content-warning-' + level + '-' + index"
            class="calendar-content-warning">
            <calendar-warning-item
              v-for="(warning, subindex) of warningsGroup"
              :key="'calendar-content-warning-item-' + warning.id + '-' + subindex"
              :level="level.toLowerCase()"
              :date-from="dateFrom"
              :date-to="dateTo"
              :warning-data="warning" />
          </div>
        </template>
      </template>
    </div>

    <div :class="{ 'calendar-content-aircrafts': true, 'is-loading': !aircraftsLoaded || !flightsLoaded }">
      <div
        v-for="(aircraft, index) of aircrafts"
        :key="'calendar-content-aircraft-' + index"
        :class="{ 'calendar-content-aircraft': true, 'is-selected': aircraft.id === selectedAircraft }">
        <calendar-flight-item
          v-for="(flight, subindex) of getFlights(aircraft.id)"
          :key="'calendar-content-aircraft-item-' + flight.id + '-' + subindex"
          :aircraft-regin-mark="aircraft.regin_mark"
          :aircraft-call-sign="aircraft.call_sign"
          :date-from="dateFrom"
          :date-to="dateTo"
          :flight-data="flight"
          :always-show-icao="alwaysShowIcao"
          :selected-flight="selectedFlight"
          :zoom-level="zoom" />
      </div>
    </div>

    <calendar-axis-x
      :zoom="zoom"
      :days-count="daysCount"
      :date-from="dateFrom"
      :date-to="dateTo" />

    <calendar-current-time
      :date-from="dateFrom"
      :date-to="dateTo"
      :current-date-time="currentDateTime" />
  </div>
</template>

<script>
import CalendarAxisX from './CalendarAxisX.vue';
import CalendarCurrentTime from './CalendarCurrentTime.vue';
import CalendarFlightItem from './CalendarFlightItem.vue';
import CalendarHelpers from './CalendarHelpers.js';
import CalendarWarningItem from './CalendarWarningItem.vue';

export default {
  name: 'calendar-content',
  props: [
    'alwaysShowIcao',
    'aircrafts',
    'aircraftsLoaded',
    'dateFrom',
    'dateTo',
    'flights',
    'flightsLoaded',
    'notes',
    'notesLoaded',
    'notesVisible',
    'selectedAircraft',
    'warnings',
    'warningsLoaded',
    'warningsVisible',
    'zoom'
  ],
  components: {
    'calendar-axis-x': CalendarAxisX,
    'calendar-current-time': CalendarCurrentTime,
    'calendar-flight-item': CalendarFlightItem,
    'calendar-warning-item': CalendarWarningItem
  },
  warningLevels: [
    'HIGH',
    'MEDIUM'
  ],
  computed: {
    daysCount () {
      return CalendarHelpers.getDaysInPeriod(new Date(this.dateFrom), this.zoom);
    },
    warningsAmountIsOdd () {
      if (!this.warnings) {
        return 0;
      }

      let sum = 0;

      if (this.warnings['HIGH']) {
        sum += this.warnings['HIGH'].length;
      }

      if (this.warnings['MEDIUM']) {
        sum += this.warnings['MEDIUM'].length;
      }

      return sum % 2;
    }
  },
  data () {
    return {
      currentDateTime: 0,
      currentDateTimeInterval: false,
      selectedFlight: 0
    };
  },
  mounted () {
    this.calculateCurrentTime();
    this.$bus.$on('flight-details-display', this.setSelectedFlight);
    this.$bus.$on('flight-details-close', this.resetSelectedFlight);
  },
  methods: {
    calculateCurrentTime () {
      this.currentDateTime = new Date();

      this.currentDateTimeInterval = setTimeout(() => {
        this.calculateCurrentTime();
      }, 1.5 * 60 * 1000);
    },
    getFlights (aircraftID) {
      return this.flights.filter(flight => flight.aircraft_id === aircraftID);
    },
    setSelectedFlight (id) {
      this.selectedFlight = parseInt(id, 10);
    },
    resetSelectedFlight () {
      this.selectedFlight = 0;
    }
  },
  beforeDestroy () {
    clearTimeout(this.currentDateTimeInterval);
    this.$bus.$off('flight-details-display', this.setSelectedFlight);
    this.$bus.$off('flight-details-close', this.resetSelectedFlight);
  }
};
</script>

<style lang="scss" scoped>
.calendar-content {
  background: #fff;
  min-height: calc(100vh - 48px);
  padding-bottom: 40px;
  padding-top: 90px;
  position: relative;
  transition: width .25s ease-out;
  width: calc(100% - 200px);
  z-index: 1;

  &.has-visible-notes {
    padding-top: 133px;
  }

  &.flight-details-is-open {
    width: calc(100% - 600px);
  }

  &-warning {
    border-bottom: 1px solid #eee;
    height: 52px;
    position: relative;

    &:nth-child(2n) {
      background: rgba(0, 0, 0, .05);
    }
  }

  &-warnings {
    position: relative;
    transition: opacity .25s ease-out;
    z-index: 2;

    &.is-loading {
      opacity: .5;
    }

    &[data-is-odd="1"] {
      & + .calendar-content-aircrafts {
        .calendar-content-aircraft:nth-child(2n + 1) {
          background: rgba(0, 0, 0, .05);
        }
      }
    }

    &[data-is-odd="0"] {
      & + .calendar-content-aircrafts {
        .calendar-content-aircraft:nth-child(2n) {
          background: rgba(0, 0, 0, .05);
        }
      }
    }
  }

  &-aircraft {
    border-bottom: 1px solid #eee;
    height: 100px;
    position: relative;

    &.is-selected {
      background: rgba(255, 252, 187, .33)!important;
    }
  }

  &-aircrafts{
    position: relative;
    transition: opacity .25s ease-out;
    z-index: 1;

    &.is-loading {
      opacity: .5;
    }

    &:first-child {
      .calendar-content-aircraft:nth-child(2n) {
        background: rgba(0, 0, 0, .05);
      }
    }
  }
}
</style>
